import { useCallback, useEffect, useState } from "react";
import {
  getNotifications,
  getUnreadCount,
} from "../../../services/notificationService";
import {
  EVENT_TYPE_ACTION_LABELS,
  NOTIFICATION_ACTION_CONFIG,
} from "../notificationConstants";

import type {
  Alert,
  AlertAction,
  AlertType,
  ApiNotificationAction,
  ApiNotificationItem,
  TypeCounts,
  UrgencyLevel,
} from "../types/notification.types";

// ── Lookup tables ──────────────────────────────────────────────────────────────
const EVENT_KEY_TO_ALERT_TYPE: Record<string, AlertType> = {
  // Draw & Disbursement
  DRAW_REQUEST_SUBMITTED: "draw",
  DRAW_APPROVED_BY_BANK: "draw",
  DRAW_REJECTED_BY_BANK: "draw",
  DRAW_REQUEST_OWNER_SIGNED_ADMIN: "draw",
  DRAW_REQUEST_OWNER_SIGNED_GC: "draw",
  DRAW_REQUEST_OWNER_SIGNED_INSPECTOR: "draw",
  // Flags
  OVERBILLING_FLAG_RAISED: "flag",
  RESIDENTIAL_OVERBILLING_FLAG_RAISED: "flag",
  // Lien Waivers
  LIEN_WAIVER_REQUEST_SENT: "lien",
  LIEN_WAIVER_SIGNED: "lien",
  LIEN_WAIVER_OVERDUE: "overdue",
  ALL_LIEN_WAIVERS_RECEIVED: "lien",
  // Inspections
  FIELD_INSPECTION_ASSIGNED: "report",
  FIELD_INSPECTION_SUBMITTED: "report",
  RESIDENTIAL_FIELD_INSPECTION_SUBMITTED: "report",
  PROGRESS_REPORT_PUBLISHED: "report",
  // Pre-Construction
  PRE_CON_CHECKLIST_SUBMITTED: "precon",
  PRE_CON_CHECKLIST_OVERDUE: "overdue",
  PRE_CON_REPORT_APPROVED: "precon",
  SOV_SUBMITTED_ADMIN: "precon",
  SOV_SUBMITTED_LENDER: "precon",
  SOV_APPROVED_BY_BANK: "precon",
  GC_REQUIREMENTS_ACK_ADMIN: "precon",
  GC_REQUIREMENTS_ACK_LENDER: "precon",
  GC_REQUIREMENTS_ACK_OWNER: "precon",
  PRE_CON_REPORT_PUBLISHED_LENDER: "precon",
  PRE_CON_REPORT_PUBLISHED_OWNER: "precon",
  PRE_CON_REPORT_PUBLISHED_GC: "precon",
  // Change Orders
  CHANGE_ORDER_SUBMITTED_ADMIN: "draw",
  CHANGE_ORDER_SUBMITTED_LENDER: "draw",
  CHANGE_ORDER_SUBMITTED_OWNER: "draw",
  CHANGE_ORDER_APPROVED_BY_OWNER: "draw",
  CHANGE_ORDER_REJECTED_BY_OWNER: "draw",
  // Milestones
  PROJECT_MILESTONE_REACHED: "milestone",
  SUBSTANTIAL_COMPLETION: "milestone",
  // Users
  USER_INVITATION_SENT: "user",
  USER_ACCOUNT_ACTIVATED: "user",
  USER_INVITATION_RESENT: "user",
  USER_FORGOT_PASSWORD: "user",
  USER_LOGIN_2FA_CODE: "user",
  USER_LOGIN_ALERT: "user",
  USER_PHONE_VERIFY_CODE: "user",
};
const EVENT_TYPE_TO_ALERT_TYPE: Record<string, AlertType> = {
  pre_construction: "precon",
  lien_waiver: "lien",
  draw: "draw",
  draw_disbursement: "draw",
  change_order: "draw",
  report: "report",
  inspection: "report",
  flag: "flag",
  flagged: "flag",
  overdue: "overdue",
  milestone: "milestone",
  user: "user",
};

export const ALERT_TYPE_TO_EVENT_TYPE: Partial<Record<string, string>> = {
  draw: "draw_disbursement",
  precon: "pre_construction",
  lien: "lien_waiver",
  milestone: "milestone",
  user: "user",
  overdue: "overdue",
  flag: "flagged",
  report: "report",
};

const DEFAULT_TYPE_COUNTS: TypeCounts = {
  all: 0,
  flag: 0,
  overdue: 0,
  draw: 0,
  lien: 0,
  report: 0,
  precon: 0,
  milestone: 0,
  user: 0,
};

const SUMMARY_KEY_TO_ALERT_TYPE: Partial<Record<string, AlertType>> = {
  draw_disbursement: "draw",
  pre_construction: "precon",
  lien_waiver: "lien",
  overdue: "overdue",
  report: "report",
  flag: "flag",
  flagged: "flag",
  milestone: "milestone",
  user: "user",
};

const parseSummaryCounts = (summary: Record<string, number>): TypeCounts => {
  const counts: TypeCounts = {
    ...DEFAULT_TYPE_COUNTS,
    all: summary.totalCount ?? 0,
  };
  Object.entries(summary).forEach(([key, val]) => {
    const alertType = SUMMARY_KEY_TO_ALERT_TYPE[key];
    if (alertType)
      counts[alertType] = (counts[alertType] ?? 0) + (val as number);
  });
  return counts;
};

const TYPE_VISUALS: Record<
  AlertType,
  { icon: string; ic: string; tc: string }
> = {
  flag: { icon: "🚩", ic: "ni-flag", tc: "tag-flag" },
  overdue: { icon: "⏰", ic: "ni-overdue", tc: "tag-overdue" },
  draw: { icon: "💰", ic: "ni-draw", tc: "tag-draw" },
  lien: { icon: "✍️", ic: "ni-lien", tc: "tag-lien" },
  report: { icon: "📋", ic: "ni-report", tc: "tag-report" },
  precon: { icon: "📁", ic: "ni-precon", tc: "tag-precon" },
  milestone: { icon: "🏁", ic: "ni-milestone", tc: "tag-milestone" },
  user: { icon: "👤", ic: "ni-user", tc: "tag-user" },
};

const VALID_URGENCY = new Set<UrgencyLevel>(["urgent", "action", "ok", "info"]);
const toUrgency = (val?: string): UrgencyLevel =>
  VALID_URGENCY.has(val as UrgencyLevel) ? (val as UrgencyLevel) : "info";

const DEFAULT_ACTIONS: Record<AlertType, AlertAction[]> = {
  flag: [
    { label: "View Draw", action: "view_draw", primary: true },
    { label: "Dismiss", action: "dismiss" },
  ],
  overdue: [
    { label: "Send Reminder", action: "send_reminder", primary: true },
    { label: "Mark Resolved", action: "mark_resolved" },
  ],
  draw: [{ label: "Review Draw", action: "review_draw", primary: true }],
  lien: [{ label: "View Waiver", action: "view_waiver", primary: true }],
  report: [{ label: "View Report", action: "view_report", primary: true }],
  precon: [
    { label: "Review Checklist", action: "review_checklist", primary: true },
  ],
  milestone: [{ label: "View Project", action: "view_project" }],
  user: [{ label: "View User", action: "view_user" }],
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatRelativeTime = (isoDate: string): string => {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffM = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);
  const diffD = Math.floor(diffMs / 86_400_000);

  if (diffM < 1) return "Just now";
  if (diffH < 1) return `${diffM} min${diffM > 1 ? "s" : ""} ago`;
  if (diffH < 24) return `${diffH} hour${diffH > 1 ? "s" : ""} ago`;
  if (diffD === 1) return "Yesterday";
  if (diffD < 7) return `${diffD} days ago`;
  if (diffD < 14) return "1 week ago";
  return `${Math.floor(diffD / 7)} weeks ago`;
};

const getDaysAgo = (isoDate: string): number => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const d = new Date(isoDate);
  d.setHours(0, 0, 0, 0);
  return Math.round((todayStart.getTime() - d.getTime()) / 86_400_000);
};

// ── Normalizer ─────────────────────────────────────────────────────────────────

const normalizeItem = (item: ApiNotificationItem): Alert => {
  const { notification, receiverReadAt, createdAt } = item;
  const settings = notification.notificationSettings;

  const isOverdue = settings.eventAction === "overdue";
  const alertType: AlertType = isOverdue
    ? "overdue"
    : (EVENT_KEY_TO_ALERT_TYPE[settings.eventKey] ??
      EVENT_TYPE_TO_ALERT_TYPE[settings.eventType] ??
      "report");

  const visuals = TYPE_VISUALS[alertType];
  const daysAgo = getDaysAgo(createdAt);

  return {
    id: item.id,
    type: alertType,
    unread: receiverReadAt === null,
    urgency: toUrgency(settings.eventAlertType),
    icon: visuals.icon,
    ic: visuals.ic,
    tc: visuals.tc,
    tl:
      EVENT_TYPE_ACTION_LABELS[settings.eventType]?.[settings.eventAction] ??
      EVENT_TYPE_ACTION_LABELS[settings.eventKey]?.[settings.eventAction] ??
      Object.values(EVENT_TYPE_ACTION_LABELS[settings.eventKey] ?? {})[0] ??
      settings.notificationName ??
      settings.eventKey,
    title: notification.title,
    detail: stripHtml(notification.message),
    project: notification.payload.project_name ?? "",
    time: formatRelativeTime(createdAt),
    ts: daysAgo,
    roles: ["Admin"],
    actions: notification.actions?.length
      ? notification.actions.map((a: ApiNotificationAction, i: number) => ({
          label: NOTIFICATION_ACTION_CONFIG[a.action]?.label ?? a.label,
          action: a.action,
          primary: i === 0,
        }))
      : DEFAULT_ACTIONS[alertType],
    platformLink: notification.payload.platform_link,
    subPhone: notification.payload.sub_phone,
    drawId: notification.payload.draw_id,
    lienWaiverId: notification.payload.lien_waiver_id,
    projectId: notification.payload.project_id ?? undefined,
    userId: notification.payload.userId ?? undefined,
    email: notification.payload.email ?? undefined,
    bankEmail: notification.payload.bank_email ?? undefined,
    reportId: notification.payload?.report_id ?? undefined,
    inspectorEmail: notification.payload.inspector_email || undefined,
    inspectorPhone: notification.payload.inspector_phone || undefined,
    gcEmail: notification.payload.gc_email || undefined,
    gcPhone: notification.payload.gc_phone || undefined,
    createdAt,
  };
};

// ── Hook ──────────────────────────────────────────────────────────────────────

interface NotificationParams {
  search?: string;
  isRead?: boolean;
  projectId?: string;
  viewAs?: string;
  eventType?: string;
  page?: number;
  limit?: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UseNotificationsResult {
  alerts: Alert[];
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  typeCounts: TypeCounts;
  unreadCount: number;
  refetch: () => void;
  refreshUnreadCount: () => Promise<void>;
}

const DEFAULT_PAGINATION: Pagination = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
};

export default function useNotifications(
  params: NotificationParams = {},
): UseNotificationsResult {
  const {
    search = "",
    isRead,
    projectId,
    viewAs,
    eventType,
    page = 1,
    limit = 10,
  } = params;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
  const [typeCounts, setTypeCounts] = useState<TypeCounts>(DEFAULT_TYPE_COUNTS);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams: Record<string, unknown> = { page, limit };

      if (search.trim()) queryParams.q = search.trim();
      if (isRead !== undefined) queryParams.isRead = isRead;
      if (projectId) queryParams.projectId = projectId;
      if (viewAs !== "admin") queryParams.viewAs = viewAs;
      if (eventType) queryParams.eventType = eventType;

      const res: any = await getNotifications(queryParams);

      if (res?.error) {
        setAlerts([]);
        return;
      }

      const body = res?.data ?? {};

      const items: ApiNotificationItem[] = Array.isArray(body?.data?.data)
        ? body.data.data
        : Array.isArray(body?.data)
          ? body.data
          : [];

      const pg = body?.pagination ?? body?.data?.pagination;
      if (pg) setPagination(pg);

      const summary = body?.addOns?.summary ?? body?.data?.addOns?.summary;
      if (summary) setTypeCounts(parseSummaryCounts(summary));

      setAlerts(items.map(normalizeItem));

      try {
        const r: any = await getUnreadCount();
        const count = r?.data?.data?.count ?? 0;
        setUnreadCount(count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    } catch (error: any) {
      console.error("Failed to load notifications:", error);

      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load notifications",
      );
    } finally {
      setLoading(false);
    }
  }, [search, isRead, projectId, viewAs, eventType, page, limit]);
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = () => fetchNotifications();

    window.addEventListener("push-notification-received", handler);

    const swHandler = (event: MessageEvent) => {
      if (event.data?.type === "PUSH_RECEIVED") fetchNotifications();
    };
    navigator.serviceWorker?.addEventListener("message", swHandler);

    return () => {
      window.removeEventListener("push-notification-received", handler);
      navigator.serviceWorker?.removeEventListener("message", swHandler);
    };
  }, [fetchNotifications]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const r: any = await getUnreadCount();
      const count = r?.data?.data?.count ?? 0;
      setUnreadCount(count);
      window.dispatchEvent(
        new CustomEvent("notification-unread-count-changed", {
          detail: { count },
        }),
      );
    } catch {}
  }, []);

  return {
    alerts,
    loading,
    error,
    pagination,
    typeCounts,
    unreadCount,
    refetch: fetchNotifications,
    refreshUnreadCount,
  };
}
